// ABOUTME: Relation Inkoopadvies page — a calculation-input filter row and summary cards over a
// ABOUTME: TabView3 grid of stock forecast rows, backed by the /products/forecast/stock endpoint.
import { Box, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../theme";

import MConfirmation from "../../../components/global/Modals/MConfirmation";
import TabView3 from "../../../components/theme/TabView3/TabView3";
import { useTopbar } from "../../../contexts/TopbarProvider";
import { purchaseAdviceConfig } from "./purchaseAdviceConfig";
import PurchaseAdviceFilters from "./PurchaseAdviceFilters";
import PurchaseAdviceCards from "./PurchaseAdviceCards";
import PurchaseAdvice from "./views/PurchaseAdvice";

const RelationPurchaseAdvice = () => {
    const { t, ready, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const { showExtraTitle, clearExtraTitle } = useTopbar();

    // The forecast calculation inputs plus the supplier filter, lifted here so the filter row, cards
    // and grid all share them. supplierId defaults to -1 ("all"); the slice drops it when <= 0.
    // basis picks what the advice is built from: the sales forecast, or the current stock level
    // (backorders / safety stock, per the purchase strategy of each product).
    const [params, setParams] = useState({
        basis: "forecast",
        monthsBack: 2,
        daysInFuture: 30,
        includeMissedSales: false,
        supplierId: -1,
    });

    // Rows ticked under the current filters, and rows kept from earlier filters (tagged with the
    // basis they were picked under). Together they become one inbound, each with its own advised
    // quantity; kept rows are locked in the grid so a product cannot be picked twice.
    const [selectedRows, setSelectedRows] = useState([]);
    const [rememberedRows, setRememberedRows] = useState([]);
    // ShDataGrid3 keeps its own tick state and re-syncs whenever this prop changes identity, so
    // handing it a fresh empty array is what actually unticks the checkboxes.
    const [selectedItems, setSelectedItems] = useState([]);
    // Filters picked while rows are ticked wait here until the user keeps or drops the selection.
    const [pendingParams, setPendingParams] = useState(null);

    const changeParams = (update) => {
        const next = typeof update === "function" ? update(params) : update;
        if (Object.keys(next).every((key) => next[key] === params[key])) return;
        if (selectedRows.length > 0) {
            setPendingParams(next);
            return;
        }
        setParams(next);
    };

    const resetSelection = () => {
        setSelectedRows([]);
        setRememberedRows([]);
        setSelectedItems([]);
    };

    const applyPendingParams = (keepSelection) => {
        if (keepSelection) {
            setRememberedRows((prev) => [...prev, ...selectedRows.map((row) => ({ ...row, adviceBasis: params.basis }))]);
            setSelectedRows([]);
            setSelectedItems([]);
        } else {
            resetSelection();
        }
        setParams(pendingParams);
        setPendingParams(null);
    };

    useEffect(() => {
        showExtraTitle(t("purchase_advice"));

        return () => {
            clearExtraTitle();
        };
    }, [showExtraTitle, clearExtraTitle, t, i18n.language]);

    return (
        <Box sx={{ width: 1, paddingTop: 9, background: colors.bg["tertiary"] }}>
            {ready ? (
                <>
                    {/* Filter sidebar and the results card form one combined header block. */}
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "stretch",
                            gap: 3,
                            px: 3,
                            pb: 3,
                        }}
                    >
                       
                        <PurchaseAdviceFilters params={params} setParams={changeParams} />
                        <PurchaseAdviceCards params={params} setParams={setParams} />
                    </Box>

                    <TabView3
                        tabConfig={purchaseAdviceConfig}
                        sx={{
                            minHeight: "calc(100% - 400px)",
                            background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                            position: "relative",
                            zIndex: 2,
                            display: "flex",
                            flexDirection: "column",

                            "&:before": {
                                content: "''",
                                display: "block",
                                position: "absolute",
                                left: 0,
                                top: "40px",
                                zIndex: 1,
                                backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                                height: "48px",
                                width: "24px",
                                borderTopLeftRadius: "24px",
                                boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                                transition: "250ms",
                            },

                            "&:after": {
                                content: "''",
                                display: "block",
                                position: "absolute",
                                top: "40px",
                                right: 0,
                                zIndex: 1,
                                backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                                height: "48px",
                                width: "24px",
                                borderTopLeftRadius: "24px",
                                boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                                transform: "scaleX(-1)",
                                transition: "250ms",
                            },

                            "@media screen and (max-width: 47.9375em)": {
                                "&:after, &:before": {
                                    top: "-0px",
                                },
                            },
                        }}
                    >
                        <>
                            <PurchaseAdvice
                                config={purchaseAdviceConfig.purchaseAdvice}
                                params={params}
                                selectedRows={selectedRows}
                                setSelectedRows={setSelectedRows}
                                rememberedRows={rememberedRows}
                                selectedItems={selectedItems}
                                resetSelection={resetSelection}
                            />
                        </>
                    </TabView3>

                    <MConfirmation
                        open={!!pendingParams}
                        handleClose={() => setPendingParams(null)}
                        title={t("purchase_advice_keep_selection_title")}
                        content={t("purchase_advice_keep_selection_text", { count: selectedRows.length })}
                        confirmLabel={t("purchase_advice_keep_selection")}
                        confirm={() => applyPendingParams(true)}
                        ignoreCloseAfterConfirm
                        cancelLabel={t("purchase_advice_start_over")}
                        onCancel={() => applyPendingParams(false)}
                    />
                </>
            ) : null}
        </Box>
    );
};

export default RelationPurchaseAdvice;
